import type { RichTextRendererType } from '@helpcenter/field-types';
import type { CustomField, CustomFieldType } from '@helpcenter/forge-ui-utils';
import type { AjaxError } from 'rxjs/observable/dom/AjaxObservable';
import type { FieldValues, ProformaRequestTypeConfig } from 'state/persisted/request-create';
import type { TemplateForm } from '@atlassian/proforma-common-core/form-system-models';
import type { FormAnswers } from '@atlassian/proforma-form-renderer/legacy-converters';

/**
 * The expected format for proforma form data when submitting a request.
 * This format is required by the server and must include both:
 * - templateFormId: The ID of the proforma template form (from form.design.settings.templateId)
 * - answers: The form answers keyed by question ID
 */
export interface SerializedProformaFormData {
	templateFormId: number;
	answers: FormAnswers;
	/** Client-side diagnostic data; excluded from the request payload. */
	visibleFieldCount?: number;
}

// Suppressing existing violation.
// oxlint-disable-next-line no-shadow
export enum ERROR_REASON_KEYS {
	// Note: These error reason keys are not exhaustive
	// When the recaptcha validation fails
	RECAPTCHA_ERROR = 'sd.recaptcha.user.may.be.a.robot',
	// when the user is raising a request anonymously in the portal using an domain that requires signing up
	SIGNUP_REQUIRED = 'signup.required.error',
	SIGNUP_VALIDATION_ERROR = 'signup.validation.errors',
	RECAPTCHA_SHARED_KEY_NOT_SET = 'sd.recaptcha.shared.key.not.set',
	RECAPTCHA_DOMAIN_MISMATCH = 'sd.recaptcha.domain.not.match',
	// When the user do not have permission to create requests using the Portal
	NEED_PERMISSION = 'sd.request.create.error.permission',
	// When the user do not have permission to create requests on behalf of other users
	NEED_PERMISSION_ON_BEHALF_OF = 'sd.request.create.error.permission.onbehalfof',
	// When user can't raise a request on behalf of a user as they need the customer role on their site managed by their organization's admins
	NEED_PERMISSION_REQUESTOR = 'sd.request.create.error.permission.requestor',
	// When user does not have permission to set issue property
	NEED_PERMISSION_SET_ISSUE_PROPERTY = 'sd.request.create.error.permission.setIssueProperty',
	// When the issue type of the request raised isn't found
	ISSUE_TYPE_NOT_FOUND = 'sd.request.create.error.issuetype.not.found',
	// When default value for a field is missing
	NO_DEFAULT_FOUND = 'sd.request.create.error.no.default.found',
	// When certain fields have errors
	FAILED_FIELD_OPERATION = 'sd.request.create.error.failed.field.operation',
	// Unable to create request because of some errors
	FAILED_MSG = 'sd.request.create.error.failed.msg',
	// When account id is not valid user's id
	UNKNOWN_ACCOUNT_ID = 'sd.request.create.error.unknown.account.id',
	// When proforma form properties are not valid
	PROFORMA_FORM_PROPERTIES_ERROR = 'sd.request.create.error.proforma.form.properties.error',
	// When user does not have permission to view the request
	NO_PERMISSION = 'sd.request.view.error.no.permission',
}

export interface ErrorWithReason {
	[errorMessage: string]: string;
}

export interface GenericError {
	[key: string]: boolean;
}

export interface FieldErrors {
	[fieldname: string]: string;
}

export interface SubmissionError {
	isGeneric: boolean;
	traceId?: string | null;
	statusCode: number;
}

export interface RequestCreateAjaxError extends AjaxError {
	response: {
		errors?: ErrorMessage[];
		reasonKey?: ERROR_REASON_KEYS;
	} | null;
}

export interface RequestDetailsResponse {
	requestDetailsBaseUrl: string;
	key: string;
	issue: { id: number };
	issueType: string;
}

export interface ErrorMessage {
	errorMessage: string;
	field?: string;
}

interface RequestCreateOrganisationResponse {
	id: number;
	name: string;
}

export interface RequestCreateResponse {
	key: string;
	id: number;
	form: Form;
	fields: Field[];
	userOrganisations: RequestCreateOrganisationResponse[];
	canRaiseOnBehalf: boolean;
	canBrowseUsers: boolean;
	requestCreateBaseUrl: string;
	requestValidateBaseUrl: string;
	calendarParams: CalendarParams;
	kbs?: KbsModel;
	projectId: number;
	canSignupCustomers: boolean;
	canSubmitWithEmailAddress: boolean;
	canCreateAttachments: boolean;
	attachmentRequiredField: boolean;
	hasGroups: boolean;
	showRecaptcha: boolean;
	siteKey: string;
	mediaApiUploadInformation?: MediaApiUploadInformation;
	hasProformaForm: boolean;
	// TypeScript upgrade (v4.4.3). Please correct when you revisit this code.
	// eslint-disable-next-line @typescript-eslint/ban-types
	proformaTemplateForm: TemplateForm | {};
	proforma: ProformaRequestTypeConfig;
	issueType?: {
		id: string;
		name: string;
	};
}

export interface EnrichedRequestCreateResponse extends RequestCreateResponse {
	portalId: number | undefined;
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	lastSavedFormData?: { [key: string]: any };
	hydratedUrlParamOverrides: FieldValues;
}

// todo: remove in REQIN-345
export type MediaApiUploadInformation = MediaApiUploadToken | MediaApiError;
// todo: remove in REQIN-345
export interface MediaApiUploadToken {
	token: string;
	targetCollection: string;
	endpointUrl: string;
	clientId: string;
	tokenDurationInMins: number;
}

export interface MediaApiError {
	error: string;
}

export interface KbsModel {
	projectKey: string;
	kbEnabled: boolean;
	kbLink: {
		appLinkId: string;
		appLinkName: string;
		spaceKey: string;
		spaceName: string;
	};
	labels: string[];
	serviceDeskId: number;
}

export interface CalendarParams {
	date: string;
	dateIfFormat: string;
	dateTimeIfFormat: string;
	firstDay: number;
	timeFormat: string;
	useISO8601WeekNumbers: boolean;
}

export interface Form {
	callToAction: string;
	descriptionHtml: string;
	icon: number; // Only used as a fallback for iconUrl. To be removed in JDW-292
	iconUrl: string;
	instructions: string;
	intro: string;
	key: string;
	name: string;
}

export interface FieldValue {
	value: string;
	label: string;
	selected: boolean;
	children: FieldValue[];
}

export interface Field {
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	defaultObject?: any;
	defaultObjectAsJson?: string;
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	defaultList?: any[];
	defaultListAsJson?: string;
	description: string;
	descriptionHtml: string;
	displayed: boolean;
	fieldConfigId: string;
	fieldId: string;
	fieldType: string;
	// Remove '?' optional on isUiModificationsSupportEnabled, ditto_uim_-_jsm_support_main FF cleanup
	jiraFieldType?: string;
	forge?: { extension?: CustomField | CustomFieldType };
	label: string;
	required: boolean;
	values?: FieldValue[];
	noneOptionRequired?: boolean;
	rendererType?: RichTextRendererType;
	autoCompleteUrl?: string;
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	portalFieldConfiguration?: any;
}
